// client/src/pages/Admin/ProjectEditor.tsx
import React, { useState } from 'react';
import { 
    Project, 
    ProjectCategory, 
    ProjectCategoryLabels, 
    ProjectStatus,
    ProjectImpact,
    CreateProjectDTO 
} from '../../shared/models/Project';
import { FileUpload } from '../../components/FileUpload';
import './ProjectEditor.css';

interface ProjectEditorProps {
    project?: Project;
    onSave: () => void;
    onCancel: () => void;
}

export const ProjectEditor: React.FC<ProjectEditorProps> = ({ 
    project, 
    onSave, 
    onCancel 
}) => {
    const isEditing = !!project;

    const [form, setForm] = useState<Partial<CreateProjectDTO>>({
        title: project?.title || '',
        subtitle: project?.subtitle || '',
        category: project?.category || ProjectCategory.GOVERNANCE,
        description: project?.description || '',
        fullDescription: project?.fullDescription || '',
        objectives: project?.objectives || [''],
        approach: project?.approach || '',
        expectedOutcomes: project?.expectedOutcomes || [''],
        impact: project?.impact || ProjectImpact.MEDIUM,
        startDate: project?.startDate ? new Date(project.startDate) : new Date(),
        endDate: project?.endDate ? new Date(project.endDate) : undefined,
        isOngoing: project?.isOngoing || false,
        regions: project?.regions || [''],
        districts: project?.districts || [''],
        tags: project?.tags || [],
        featured: project?.featured || false,
        featuredImage: project?.featuredImage || { url: '', alt: '', caption: '', isFeatured: true },
        status: project?.status || ProjectStatus.DRAFT
    });

    const [loading, setLoading] = useState(false);

    const getToken = () => localStorage.getItem('adminToken');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const url = isEditing 
                ? '/.netlify/functions/admin-project-update'
                : '/.netlify/functions/admin-project-create';
            
            const method = isEditing ? 'PUT' : 'POST';

            const payload = isEditing 
                ? { id: project!._id, ...form }
                : form;

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to save project');
            }

            onSave();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to save project');
        } finally {
            setLoading(false);
        }
    };

    const handleArrayChange = (field: string, index: number, value: string) => {
        const current = form[field as keyof typeof form] as string[] || [];
        const newArray = [...current];
        newArray[index] = value;
        setForm({ ...form, [field]: newArray });
    };

    const addArrayItem = (field: string) => {
        const current = form[field as keyof typeof form] as string[] || [];
        setForm({ ...form, [field]: [...current, ''] });
    };

    const removeArrayItem = (field: string, index: number) => {
        const current = form[field as keyof typeof form] as string[] || [];
        if (current.length <= 1) return;
        const newArray = current.filter((_, i) => i !== index);
        setForm({ ...form, [field]: newArray });
    };

    const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
        setForm({ ...form, tags });
    };

    const handleFeaturedImageUpload = (fileInfo: { url: string; publicId: string; name: string; size: number; type: string }) => {
        setForm({
            ...form,
            featuredImage: {
                url: fileInfo.url,
                alt: fileInfo.name,
                caption: fileInfo.name,
                isFeatured: true
            }
        });
    };

    const removeFeaturedImage = () => {
        setForm({
            ...form,
            featuredImage: { url: '', alt: '', caption: '', isFeatured: false }
        });
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <h2>{isEditing ? 'Edit Project' : 'Create New Project'}</h2>
                
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3>Basic Information</h3>
                        
                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                id="title"
                                name="title"
                                type="text"
                                value={form.title}
                                onChange={e => setForm({ ...form, title: e.target.value })}
                                required
                                placeholder="Enter project title"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="subtitle">Subtitle</label>
                            <input
                                id="subtitle"
                                name="subtitle"
                                type="text"
                                value={form.subtitle || ''}
                                onChange={e => setForm({ ...form, subtitle: e.target.value })}
                                placeholder="Short subtitle or tagline"
                            />
                        </div>

                        <div className="form-row">
                                <div className="form-group">
                                <label htmlFor="category">Category *</label>
                                <select
                                    id="category"
                                    name="category"
                                    value={form.category}
                                    onChange={e => setForm({ ...form, category: e.target.value as ProjectCategory })}
                                    required
                                >
                                    {Object.entries(ProjectCategoryLabels).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={form.status}
                                    onChange={e => setForm({ ...form, status: e.target.value as ProjectStatus })}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                    <option value="coming_soon">Coming Soon</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="impact">Impact Level</label>
                                <select
                                    id="impact"
                                    name="impact"
                                    value={form.impact}
                                    onChange={e => setForm({ ...form, impact: e.target.value as ProjectImpact })}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="startDate">Start Date</label>
                                <input
                                    id="startDate"
                                    name="startDate"
                                    type="date"
                                    value={form.startDate ? new Date(form.startDate).toISOString().split('T')[0] : ''}
                                    onChange={e => setForm({ ...form, startDate: new Date(e.target.value) })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="endDate">End Date</label>
                                <input
                                    id="endDate"
                                    name="endDate"
                                    type="date"
                                    value={form.endDate ? new Date(form.endDate).toISOString().split('T')[0] : ''}
                                    onChange={e => setForm({ ...form, endDate: e.target.value ? new Date(e.target.value) : undefined })}
                                    disabled={form.isOngoing}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="isOngoing">
                                    <input
                                        id="isOngoing"
                                        name="isOngoing"
                                        type="checkbox"
                                        checked={form.isOngoing}
                                        onChange={e => {
                                            setForm({ ...form, isOngoing: e.target.checked });
                                            if (e.target.checked) {
                                                setForm({ ...form, isOngoing: true, endDate: undefined });
                                            }
                                        }}
                                    />
                                    Ongoing Project
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Content</h3>

                        <div className="form-group">
                            <label htmlFor="description">Short Description *</label>
                            <textarea
                                id="description"
                                name="description"
                                value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                                required
                                rows={3}
                                placeholder="Brief description (shown on project cards)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="fullDescription">Full Description</label>
                            <textarea
                                id="fullDescription"
                                name="fullDescription"
                                value={form.fullDescription || ''}
                                onChange={e => setForm({ ...form, fullDescription: e.target.value })}
                                rows={5}
                                placeholder="Detailed project description (shown on project detail page)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="approach">Approach / Methodology</label>
                            <textarea
                                id="approach"
                                name="approach"
                                value={form.approach || ''}
                                onChange={e => setForm({ ...form, approach: e.target.value })}
                                rows={3}
                                placeholder="How the project is being implemented"
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Featured Image</h3>

                        <div className="form-group">
                            <label>Project Image</label>
                            <div className="file-upload-area">
                                <FileUpload
                                    accept="image/*"
                                    label="📷 Upload project image"
                                    onUpload={handleFeaturedImageUpload}
                                />

                                {form.featuredImage?.url && (
                                    <>
                                        <div className="uploaded-file-info">
                                            <span className="file-icon">🖼️</span>
                                            <span className="file-name">{form.featuredImage.alt || 'Project image'}</span>
                                            <button
                                                type="button"
                                                className="remove-file-btn"
                                                onClick={removeFeaturedImage}
                                            >
                                                ✕ Remove
                                            </button>
                                        </div>
                                        <div className="image-preview-wrapper">
                                            <img
                                                src={form.featuredImage.url}
                                                alt={form.featuredImage.alt || 'Project image preview'}
                                                className="image-preview"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Objectives</h3>
                        {form.objectives?.map((objective, index) => (
                            <div key={index} className="array-row">
                                <input
                                    id={`objectives-${index}`}
                                    name="objectives"
                                    type="text"
                                    value={objective}
                                    onChange={e => handleArrayChange('objectives', index, e.target.value)}
                                    placeholder={`Objective ${index + 1}`}
                                />
                                {form.objectives!.length > 1 && (
                                    <button type="button" onClick={() => removeArrayItem('objectives', index)}>
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addArrayItem('objectives')} className="btn-add">
                            + Add Objective
                        </button>
                    </div>

                    <div className="form-section">
                        <h3>Expected Outcomes</h3>
                        {form.expectedOutcomes?.map((outcome, index) => (
                            <div key={index} className="array-row">
                                <input
                                    id={`expectedOutcomes-${index}`}
                                    name="expectedOutcomes"
                                    type="text"
                                    value={outcome}
                                    onChange={e => handleArrayChange('expectedOutcomes', index, e.target.value)}
                                    placeholder={`Outcome ${index + 1}`}
                                />
                                {form.expectedOutcomes!.length > 1 && (
                                    <button type="button" onClick={() => removeArrayItem('expectedOutcomes', index)}>
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addArrayItem('expectedOutcomes')} className="btn-add">
                            + Add Expected Outcome
                        </button>
                    </div>

                    <div className="form-section">
                        <h3>Location</h3>

                            <div className="form-group">
                            <label>Regions</label>
                            {form.regions?.map((region, index) => (
                                <div key={index} className="array-row">
                                    <input
                                        id={`regions-${index}`}
                                        name="regions"
                                        type="text"
                                        value={region}
                                        onChange={e => handleArrayChange('regions', index, e.target.value)}
                                        placeholder="e.g., Lusaka, Copperbelt"
                                    />
                                    {form.regions!.length > 1 && (
                                        <button type="button" onClick={() => removeArrayItem('regions', index)}>
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => addArrayItem('regions')} className="btn-add">
                                + Add Region
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Districts</label>
                            {form.districts?.map((district, index) => (
                                <div key={index} className="array-row">
                                    <input
                                        id={`districts-${index}`}
                                        name="districts"
                                        type="text"
                                        value={district}
                                        onChange={e => handleArrayChange('districts', index, e.target.value)}
                                        placeholder="e.g., Lusaka, Ndola"
                                    />
                                    {form.districts!.length > 1 && (
                                        <button type="button" onClick={() => removeArrayItem('districts', index)}>
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => addArrayItem('districts')} className="btn-add">
                                + Add District
                            </button>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Tags & Visibility</h3>

                        <div className="form-group">
                            <label htmlFor="tags">Tags</label>
                            <input
                                id="tags"
                                name="tags"
                                type="text"
                                value={form.tags?.join(', ') || ''}
                                onChange={handleTagChange}
                                placeholder="Comma-separated tags (e.g., governance, policy, youth)"
                            />
                        </div>

                        <div className="checkbox-group">
                            <label htmlFor="featured">
                                <input
                                    id="featured"
                                    name="featured"
                                    type="checkbox"
                                    checked={form.featured}
                                    onChange={e => setForm({ ...form, featured: e.target.checked })}
                                />
                                Featured Project
                            </label>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : isEditing ? 'Update Project' : 'Create Project'}
                        </button>
                        <button type="button" onClick={onCancel} className="btn-secondary">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};